import {
  Send20Regular,
  Attach20Regular,
  Edit20Regular,
  Delete20Regular,
  ArrowReply20Regular,
  Person20Regular,
  CommentMultiple20Regular,
  Document20Regular,
} from "@fluentui/react-icons";
import { useState, useRef, useEffect, useMemo } from "react";
import { APP_LOCALE } from "../config/locale";

// Types
export interface Comment {
  id: string;
  author: {
    name: string;
    avatar?: string;
    isSystem?: boolean;
  };
  content: string;
  timestamp: Date;
  attachments?: CommentAttachment[];
  isSystem?: boolean;
  systemType?: "status_change" | "document_upload" | "value_update" | "other";
  canEdit?: boolean;
}

export interface CommentAttachment {
  id: string;
  name: string;
  url?: string;
  type: string;
}

interface CommentsProps {
  comments: Comment[];
  onAddComment: (content: string, attachments?: File[]) => void;
  onEditComment?: (commentId: string, newContent: string) => void | Promise<void>;
  onDeleteComment?: (commentId: string) => void;
  onReplyComment?: (commentId: string, content: string) => void;
  isLoading?: boolean;
  subtitle?: string;
  currentUser?: {
    name: string;
    avatar?: string;
  };
  showCount?: boolean;
  showIcon?: boolean;
}

// Helper function to format timestamp
function formatTimestamp(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "agora mesmo";
  if (diffMins < 60) return `há ${diffMins} minuto${diffMins > 1 ? "s" : ""}`;
  if (diffHours < 24)
    return `há ${diffHours} hora${diffHours > 1 ? "s" : ""}`;
  if (diffDays === 1) {
    return `Ontem às ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  }
  if (diffDays < 7) return `há ${diffDays} dias`;

  return date.toLocaleDateString(APP_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Comment Loading Skeleton
function CommentLoading() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3 animate-pulse">
          {/* Avatar skeleton */}
          <div
            className="w-8 h-8 rounded-full bg-accent flex-shrink-0"
            style={{ borderRadius: "var(--radius-full)" }}
          />
          <div className="flex-1 space-y-2">
            {/* Name and timestamp skeleton */}
            <div className="flex items-center gap-2">
              <div className="h-4 w-24 bg-accent rounded" />
              <div className="h-3 w-16 bg-accent/70 rounded" />
            </div>
            {/* Content skeleton */}
            <div className="space-y-1.5">
              <div className="h-3 w-full bg-accent rounded" />
              <div className="h-3 w-4/5 bg-accent rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Empty State
function CommentEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div
        className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-4"
        style={{ borderRadius: "var(--radius-full)" }}
      >
        <CommentMultiple20Regular className="w-6 h-6 text-secondary-text" />
      </div>
      <p
        className="text-foreground mb-1"
        style={{
          fontSize: "var(--text-base)",
          fontWeight: "var(--font-weight-semi-bold)",
        }}
      >
        Ainda não existem comentários.
      </p>
      <p
        className="text-secondary-text"
        style={{ fontSize: "var(--text-small)" }}
      >
        Seja o primeiro a comentar.
      </p>
    </div>
  );
}

// Regular Comment Item
function CommentItem({
  comment,
  onEdit,
  onDelete,
  onReply,
}: {
  comment: Comment;
  onEdit?: (commentId: string, newContent: string) => void | Promise<void>;
  onDelete?: (commentId: string) => void;
  onReply?: (commentId: string) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(comment.content);
  const [isSaving, setIsSaving] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const canEdit = comment.canEdit ?? false;

  useEffect(() => {
    if (!isEditing) {
      setDraft(comment.content);
    }
  }, [comment.content, isEditing]);

  useEffect(() => {
    if (isEditing) {
      editRef.current?.focus();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setDraft(comment.content);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraft(comment.content);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || !onEdit) return;

    setIsSaving(true);
    try {
      await onEdit(comment.id, trimmed);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="flex gap-3 py-3 px-4 rounded transition-colors hover:bg-accent/30"
      style={{ borderRadius: "var(--radius)" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {comment.author.avatar ? (
          <img
            src={comment.author.avatar}
            alt={comment.author.name}
            className="w-8 h-8 rounded-full object-cover"
            style={{ borderRadius: "var(--radius-full)" }}
          />
        ) : (
          <div
            className="w-8 h-8 bg-primary/10 flex items-center justify-center"
            style={{ borderRadius: "var(--radius-full)" }}
          >
            <Person20Regular className="w-4 h-4 text-primary" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        {/* Header - Author, Timestamp, Actions */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-foreground"
              style={{
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              {comment.author.name}
            </span>
            <span
              className="text-secondary-text"
              style={{ fontSize: "var(--text-small)" }}
            >
              {formatTimestamp(comment.timestamp)}
            </span>
          </div>

          {/* Actions (show on hover) */}
          {isHovered && !isEditing && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {onReply && (
                <button
                  onClick={() => onReply(comment.id)}
                  className="p-1.5 hover:bg-accent rounded transition-colors"
                  style={{ borderRadius: "var(--radius)" }}
                  title="Responder"
                >
                  <ArrowReply20Regular className="w-4 h-4 text-secondary-text" />
                </button>
              )}
              {onEdit && canEdit && (
                <button
                  onClick={handleStartEdit}
                  className="p-1.5 hover:bg-accent rounded transition-colors"
                  style={{ borderRadius: "var(--radius)" }}
                  title="Editar"
                >
                  <Edit20Regular className="w-4 h-4 text-secondary-text" />
                </button>
              )}
              {onDelete && canEdit && (
                <button
                  onClick={() => onDelete(comment.id)}
                  className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
                  style={{ borderRadius: "var(--radius)" }}
                  title="Apagar"
                >
                  <Delete20Regular className="w-4 h-4 text-destructive" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              ref={editRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-input-background border border-border rounded text-foreground placeholder:text-placeholder-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              style={{ borderRadius: "var(--radius)" }}
              placeholder="Editar comentário..."
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveEdit}
                disabled={isSaving || !draft.trim()}
                className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Guardar
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="px-3 py-1.5 bg-secondary hover:bg-accent border border-border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <p
            className="text-foreground whitespace-pre-wrap break-words"
            style={{ fontSize: "var(--text-base)" }}
          >
            {comment.content}
          </p>
        )}

        {/* Attachments */}
        {comment.attachments && comment.attachments.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {comment.attachments.map((attachment) => {
              const content = (
                <>
                  <Document20Regular className="w-4 h-4 text-primary flex-shrink-0" />
                  <span
                    className="text-foreground truncate max-w-[200px]"
                    style={{ fontSize: "var(--text-small)" }}
                  >
                    {attachment.name}
                  </span>
                </>
              );

              if (!attachment.url) {
                return (
                  <div
                    key={attachment.id}
                    className="flex items-center gap-2 px-3 py-2 bg-accent rounded"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {content}
                  </div>
                );
              }

              return (
                <a
                  key={attachment.id}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-accent hover:bg-accent/70 rounded transition-colors"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  {content}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Comment Input
function CommentInput({
  onSubmit,
  currentUser,
}: {
  onSubmit: (content: string, files?: File[]) => void;
  currentUser?: { name: string; avatar?: string };
}) {
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const maxTextareaHeight = 240;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      const previousHeight = textareaRef.current.offsetHeight;
      textareaRef.current.style.height = "auto";
      const nextHeight = Math.min(
        Math.max(textareaRef.current.scrollHeight, previousHeight),
        maxTextareaHeight,
      );
      textareaRef.current.style.height = `${nextHeight}px`;
      textareaRef.current.style.overflowY =
        textareaRef.current.scrollHeight > nextHeight
          ? "auto"
          : "hidden";
    }
  }, [content]);

  const handleSubmit = () => {
    const texto = content.trim();
    if (texto || attachments.length > 0) {
      const mensagem = texto || "Envio de anexo";
      onSubmit(mensagem, attachments);
      setContent("");
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.overflowY = "hidden";
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments([...attachments, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <div className="border-t border-border pt-4">
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {currentUser?.avatar ? (
            <img
              src={currentUser.avatar}
              alt={currentUser.name}
              className="w-8 h-8 rounded-full object-cover"
              style={{ borderRadius: "var(--radius-full)" }}
            />
          ) : (
            <div
              className="w-8 h-8 bg-primary/10 flex items-center justify-center"
              style={{ borderRadius: "var(--radius-full)" }}
            >
              <Person20Regular className="w-4 h-4 text-primary" />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="flex-1">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escreva um comentário…"
              rows={1}
              className="w-full px-4 py-3 bg-input-background border border-border rounded text-foreground placeholder:text-placeholder-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y min-h-[48px] max-h-[240px] overflow-y-auto"
              style={{ borderRadius: "var(--radius)" }}
            />
          </div>

          {/* Attachments Preview */}
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-2 bg-accent rounded"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <Document20Regular className="w-4 h-4 text-primary flex-shrink-0" />
                  <span
                    className="text-foreground text-sm truncate max-w-[150px]"
                    style={{ fontSize: "var(--text-small)" }}
                  >
                    {file.name}
                  </span>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="text-secondary-text hover:text-destructive transition-colors"
                  >
                    <Delete20Regular className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 mt-2">
            {/* Attach Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 hover:bg-accent rounded transition-colors"
              style={{ borderRadius: "var(--radius)" }}
              title="Anexar ficheiro"
            >
              <Attach20Regular className="w-5 h-5 text-secondary-text" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.dwg"
            />

            {/* Send Button */}
            <button
              onClick={handleSubmit}
              disabled={!content.trim() && attachments.length === 0}
              className="ml-auto px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              <Send20Regular className="w-4 h-4" />
              <span style={{ fontSize: "var(--text-base)" }}>Enviar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Comments Component
export function Comments({
  comments,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onReplyComment,
  isLoading = false,
  subtitle,
  currentUser,
  showCount = true,
  showIcon = true,
}: CommentsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const visibleComments = useMemo(() => {
    return comments
      .filter((comment) => !comment.isSystem)
      .sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
      );
  }, [comments]);

  useEffect(() => {
    const container = listRef.current;
    if (!container) {
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
    return () => window.cancelAnimationFrame(handle);
  }, [visibleComments.length, isLoading]);

  const handleEditComment = async (
    commentId: string,
    newContent: string,
  ) => {
    await onEditComment?.(commentId, newContent);
  };

  const handleDeleteComment = (commentId: string) => {
    if (onDeleteComment) {
      const confirmed = window.confirm(
        "Tem a certeza que deseja apagar este comentário?",
      );
      if (confirmed) {
        onDeleteComment(commentId);
      }
    }
  };

  const handleReplyComment = (commentId: string) => {
    if (onReplyComment) {
      // In a real implementation, this would focus the input or create a threaded reply
      const replyContent = prompt("Responder ao comentário:");
      if (replyContent) {
        onReplyComment(commentId, replyContent);
      }
    }
  };

  return (
    <div
      className="bg-card border border-border p-6 h-full flex flex-col min-h-0"
      style={{ borderRadius: "var(--radius-card)" }}
    >
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            {showIcon && (
              <CommentMultiple20Regular className="w-5 h-5 text-primary" />
            )}
            <h2
              className="text-foreground"
              style={{
                fontSize: "var(--text-section-title)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              Comentários
            </h2>
          </div>
          {showCount && (
            <span
              className="text-secondary-text"
              style={{
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              ({comments.filter(c => !c.isSystem).length})
            </span>
          )}
        </div>
        {subtitle && (
          <p
            className="text-secondary-text"
            style={{ fontSize: "var(--text-small)" }}
          >
            {subtitle}
          </p>
        )}
      </div>

      {/* Comments List */}
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto pr-1">
        {isLoading ? (
          <CommentLoading />
        ) : visibleComments.length === 0 ? (
          <CommentEmptyState />
        ) : (
          <div className="space-y-1">
            {visibleComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onEdit={
                  onEditComment ? handleEditComment : undefined
                }
                onDelete={
                  onDeleteComment ? handleDeleteComment : undefined
                }
                onReply={
                  onReplyComment ? handleReplyComment : undefined
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <CommentInput onSubmit={onAddComment} currentUser={currentUser} />
    </div>
  );
}
